(ns common.db.table.core
  (:require [common.db.effects.util :as effects.util]
            [common.db.table.backend :as b]
            [common.patterns.stats-daterange-select :refer [daterange-key daterange->id id->daterange]]
            [re-frame.core :as rf]))

(def next-sort-direction
  {nil :desc
   :desc :asc
   :asc nil})

(defn next-sort-state
  [{:keys [column direction]} f]
  (if (= column f)
    {:column f :direction (next-sort-direction direction)}
    {:column f :direction :desc}))

(defn initial-state
  [initial-page initial-search initial-sort initial-daterange initial-hidden initial-filters]
  {:sort (or initial-sort {:column :name :direction :asc})
   :page (or initial-page {:cursor 0 :size 10})
   :search (or initial-search {:column :name :term ""})
   :hidden (or initial-hidden nil)
   :filters (or initial-filters nil)
   :daterange (or initial-daterange nil)
   :selected-rows #{}})

(defn get-table-state
  [namespace db]
  ((juxt :page :search :sort :daterange) (namespace db)))

(defn on-change-interceptor
  [namespace backend]
  (rf/after
   (fn [db]
     (when-let [event (apply b/on-change backend (get-table-state namespace db))]
       (rf/dispatch event)))))

(defn on-init-interceptor
  [namespace backend]
  (rf/after
   (fn [db]
     (when-let [callback (apply b/on-init backend (get-table-state namespace db))]
       (effects.util/invoke-callback callback)))))

(defn register-events
  "Register events to manipulate table state."
  [qualify namespace backend initial csv-mapping daterange-options]
  ;; :namespace/init
  ;; Initialize the table state.
  (rf/reg-event-fx (qualify :init)
    [(rf/inject-cofx :localstorage [daterange-key])
     (on-init-interceptor namespace backend)]
    (fn [{:keys [db] :localstorage/keys [stats-daterange]} _]
      {:db (-> db
               (assoc namespace initial)
               (assoc-in [namespace :daterange] (or (:daterange initial) (id->daterange (keyword stats-daterange)) (:value (first daterange-options))))
               (assoc-in [namespace :daterange-options] daterange-options))}))

  ;; :namespace/sort
  ;; Modify the column on which rows are sorted.
  (rf/reg-event-db (qualify :sort-column)
    [(on-change-interceptor namespace backend)]
    (fn [db [_ f]]
      (update-in db [namespace :sort] #(next-sort-state % f))))

  ;; :namespace/page
  ;; Modify current page cursor. A page is a subset of all rows.
  (rf/reg-event-db (qualify :page)
    [(on-change-interceptor namespace backend)]
    (fn [db [_ page]]
      (assoc-in db [namespace :page :cursor] page)))

  (rf/reg-event-db (qualify :set-cursor-and-boundary)
    [(on-change-interceptor namespace backend)]
    (fn [db [_ boundary cursor]]
      {:pre [(contains? #{:starting-after :ending-before} boundary)]}
      (-> db
          (assoc-in [namespace :page :boundary] boundary)
          (assoc-in [namespace :page :cursor] cursor))))

  ;; :namespace/search-term
  ;; Modify the term on which rows are filtered.
  (rf/reg-event-db (qualify :search-term)
    (fn [db [_ value]]
      (-> db
          (assoc-in [namespace :search :term] value)
          (assoc-in [namespace :page :cursor] 0))))

  ;; :namespace/set-hidden
  ;; Modify the value on whether to show hidden elements.
  (rf/reg-event-db (qualify :set-hidden)
    (fn [db [_ value]]
      (-> db
          (assoc-in [namespace :hidden :hidden?] value)
          (assoc-in [namespace :page :cursor] 0))))

  ;; :namespace/search-column
  ;; Modify the column on which rows are filtered.
  (rf/reg-event-db (qualify :search-column)
    (fn [db [_ value]]
      (-> db
          (assoc-in [namespace :search :column] value)
          (assoc-in [namespace :page :cursor] 0))))

  ;; :namespace/confirm-search
  ;; Updates the items of the table when search is confirmed
  (rf/reg-event-fx (qualify :confirm-search)
    (fn [{:keys [db]}]
      (when-let [e (->> db
                        (get-table-state namespace)
                        (apply b/confirm-search backend))]
        {:dispatch e})))

  ;; :namespace/daterange
  ;; Modify the date range for the stats in the rows
  (rf/reg-event-fx (qualify :daterange)
    [(on-change-interceptor namespace backend)]
    (fn [{:keys [db]} [_ range]]
      {:db (assoc-in db [namespace :daterange] range)
       :fx [[:localstorage/save [daterange-key (daterange->id range)]]]}))

  (rf/reg-event-fx (qualify :apply-daterange)
    (fn [{:keys [db]}]
      (when-let [e (->> db
                        (get-table-state namespace)
                        (apply b/apply-daterange backend))]
        {:dispatch e})))

  (rf/reg-event-fx (qualify :filters)
    (fn [{:keys [db]} [_ new-filters]]
      (let [current-filter (get-in db [namespace :filters])]
        (if (= current-filter new-filters)
          {:db db}
          (let [event (apply b/on-change backend (get-table-state namespace db))]
            {:db (assoc-in db [namespace :filters] new-filters)
             :fx [(when event
                    [:dispatch event])]})))))

  (rf/reg-event-fx (qualify :refresh)
    (fn [{:keys [db]}]
      (when-let [e (->> db
                        (get-table-state namespace)
                        (apply b/on-change backend))]
        {:dispatch e})))

  (rf/reg-event-db (qualify :select-row)
    (fn [db [_ v]]
      (update-in db [namespace :selected-rows] #(-> %
                                                    (conj v)
                                                    set))))

  (rf/reg-event-db (qualify :deselect-row)
    (fn [db [_ v]]
      (update-in db [namespace :selected-rows] disj v)))

  (rf/reg-event-db (qualify :select-rows)
    (fn [db [_ value]]
      (assoc-in db [namespace :selected-rows] (set value))))

  (rf/reg-event-db (qualify :deselect-all-rows)
    (fn [db _]
      (assoc-in db [namespace :selected-rows] #{})))

  (rf/reg-event-fx (qualify :download-csv)
    [(rf/inject-cofx :sub (qualify :unpaginated-value))]
    (fn [cofx [_ name]]
      (let [coll (get-in cofx [(qualify :unpaginated-value) :value])]
        {:csv/download [coll csv-mapping name]}))))

(defn register-subs
  "Register subscriptions to retrieve table state."
  [qualify namespace backend initial value-sub]
  ;; :namespace/sort
  ;; Get column and direction on which rows are sorted.
  (rf/reg-sub (qualify :sort)
    (fn [db _]
      (get-in db [namespace :sort] (:sort initial))))

  ;; namespace/page
  ;; Get the current page cursor and size.
  (rf/reg-sub (qualify :page)
    (fn [db _]
      (get-in db [namespace :page] (:page initial))))

  ;; :namespace/search
  ;; Get table search term and column.
  (rf/reg-sub (qualify :search)
    (fn [db _]
      (get-in db [namespace :search] (:search initial))))

  (rf/reg-sub (qualify :daterange-options)
    (fn [db _]
      (get-in db [namespace :daterange-options])))

  (rf/reg-sub (qualify :daterange)
    (fn [db _]
      (get-in db [namespace :daterange] (:daterange initial))))

  (rf/reg-sub (qualify :filters)
    (fn [db _]
      (get-in db [namespace :filters] (:filters initial))))

  (rf/reg-sub (qualify :selected-rows)
    (fn [db _]
      (get-in db [namespace :selected-rows] (:selected-rows initial))))

  (rf/reg-sub (qualify :selected-row-count)
    :<- [(qualify :selected-rows)]
    (fn [selected-rows _]
      (count selected-rows)))

  (rf/reg-sub (qualify :row-selected?)
    :<- [(qualify :selected-rows)]
    (fn [selected-rows [_ value]]
      (contains? selected-rows value)))

  (rf/reg-sub (qualify :selected-state)
    :<- [(qualify :selected-rows)]
    :<- value-sub
    (fn [[selected-rows value] [_ limit]]
      (let [limited-value (if-not (nil? limit)
                            (take limit (:value value))
                            (:value value))]
        (cond
          (empty? selected-rows) :none
          (every? selected-rows limited-value) :all
          :else :partial))))

  ;; :namespace/search-term
  ;; Get table search term.
  (rf/reg-sub (qualify :search-term)
    :<- [(qualify :search)]
    (fn [search _]
      (:term search "")))

  ;; :namespace/hidden
  ;; Get table hidden map.
  (rf/reg-sub (qualify :hidden)
    (fn [db]
      (get-in db [namespace :hidden] (:hidden initial))))

  ;; :namespace/hidden?
  ;; Get table search term.
  (rf/reg-sub (qualify :hidden?)
    :<- [(qualify :hidden)]
    (fn [hidden]
      (:hidden? hidden)))

  ;; :namespace/search-column
  ;; Get table search column.
  (rf/reg-sub (qualify :search-column)
    :<- [(qualify :search)]
    (fn [search _]
      (:column search)))

  ;; :namespace/page-cursor`
  ;; Get the current page-cursor.
  (rf/reg-sub (qualify :page-cursor)
    :<- [(qualify :page)]
    (fn [page _]
      (:cursor page)))

  ;; :namespace/page-count
  ;; Get the number of pages.
  (rf/reg-sub (qualify :page-count)
    :<- [(qualify :page)]
    :<- [(qualify :search)]
    :<- value-sub
    (fn [[page search value]]
      (->> value
           (b/apply-filter backend (:column search) (:term search))
           (b/get-page-count backend page))))

  ;; :namespace/sort-direction
  ;; Get the sort direction for a column.
  ;; Can return :desc :asc or nil.
  (rf/reg-sub (qualify :sort-direction)
    :<- [(qualify :sort)]
    (fn [{:keys [column direction]} [_ key]]
      (when (= column key) direction)))

  ;; :namespace/unpaginated-value
  ;; Get the filtered and sorted, but unpaginated rows.
  (rf/reg-sub (qualify :unpaginated-value)
    :<- [(qualify :search)]
    :<- [(qualify :sort)]
    :<- [(qualify :hidden)]
    :<- value-sub
    (fn [[search sort hidden value]]
      (->> value
           (b/apply-filter backend (:column search) (:term search))
           (b/apply-sorting backend (:column sort) (:direction sort))
           (b/apply-hidden backend (:column hidden) (:hidden? hidden)))))

  ;; :namespace/value
  ;; Apply filtering, sorting and pagination to the value returned by the
  ;; `value-sub` subscription.
  (rf/reg-sub (qualify :value)
    :<- [(qualify :page)]
    :<- [(qualify :unpaginated-value)]
    (fn [[{:keys [cursor size]} value] _]
      (b/apply-pagination backend cursor size value))))

(defn register-table
  "Register event handlers and subscriptions to manage the state for a table.
   See `register-events` and `register-subs` for documentation on which events
   and subscriptions are created. Accepts a configuration map with keys:

  `namespace`      Unique keyword for qualifying table events and subscriptions.
  `value-sub`      Subscription vector returning a map containing at least a
                   :value key mapped to a vector of maps. The `backend` can
                   impose additional constraints to the return value of the
                   subscription.
  `backend`        Implementation of `TableBackend`.
  `initial-sort`   Initial sorting state.
  `initial-search` Initial search state.
  `initial-page`   Initial pagination state.
  `initial-hidden` Initial value of hidden toggle.
  `csv-mapping`    Vector defining how the table should be exported to csv.

  Example `value-sub` return value:
    {:value [{:id 1 :foo :bar} {:id 2 :foo :baz}]}

  Example `csv-mapping`:
    [[:name \"Name\" str/lower-case]
     [:created-at \"Created\" format-timestamp]]})

  Usage:
    (register-table
      {:namespace      :my-table
       :value-sub      :resource.advertisers/all
       :initial-sort   {:column :name :direction :asc}
       :initial-search {:column :name :term \"test\"}
       :initial-page   {:size 10 :cursor 0}
       :csv-mapping    csv-mapping"
  [{:keys [namespace value-sub backend initial-sort initial-search initial-hidden
           initial-page csv-mapping daterange-options initial-daterange initial-filters]}]
  (let [initial (initial-state initial-page initial-search initial-sort initial-daterange initial-hidden initial-filters)
        qualify (fn [k] (keyword namespace k))]
    (register-events qualify namespace backend initial csv-mapping daterange-options)
    (register-subs qualify namespace backend initial value-sub)))
